import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Filter from "../../../components/functional_components/Filter/Filter";
import User from "../../../services/api/User";
import {
  buildQueryString,
  checkRoles,
  exportFile,
  formatNumber,
  showError,
  showSuccess,
} from "../../../utils/utilities";
import { cloneDeep, get, toLower, includes, remove, isEmpty, split, last } from "lodash";
import {
  dbDate,
  dbDateHour,
  expenseRefundDisapprove,
  langDate,
  langDateHour,
  paginationDefaults,
} from "../../../utils/properties";
import { useDispatch, useSelector } from "react-redux";
import { Checkbox, Table, Modal, Spin, Row, Col, Input, Tooltip } from "antd";
import Button from "../../../components/functional_components/Button/PFButton";
import { EyeOutlined, CheckOutlined, DownloadOutlined, SendOutlined } from "@ant-design/icons";
import ProjectTask from "../../../services/api/ProjectTask";
import CostCentre from "../../../services/api/CostCentre";
import { setFilters } from "../../../redux/ducks/Filters";
import ExpenseRefundApproval from "../../../services/api/ExpenseRefundApproval";
import Justification from "../components/form/Justification";
import FlatRate from "../components/form/FlatRate";
import CreditCard from "../components/form/CreditCard";
import Km from "../components/form/Km";
import ExpenseRefund from "../../../services/api/ExpenseRefund";
import dayjs from "dayjs";

const { TextArea } = Input;
// const { Step } = Steps;

const DisapproveExpenseReport = () => {
  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));
  const filters = useSelector((state) => state.FiltersDuck.filters);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const currentLanguage = useSelector((state) => get(state.languageDuck, "currentLanguage", "GB"));
  const dateFormat = langDate[toLower(currentLanguage)];
  const dateHourFormat = langDateHour[toLower(currentLanguage)];
  const [downloadLoading, setDownloadLoading] = useState({ receipt: false, payment_tracking: false });
  const [state, setState] = useState({
    wbsSearchList: [],
    cdcSearchList: [],
    userInternalSearchList: [],
    dataTable: [],
    modalDetail: {
      visible: false,
      data: {},
    },
    modal: {
      visible: false,
    },
    loading: false,
    pagination: {
      currentPage: paginationDefaults.page,
      size: paginationDefaults.size,
      totalItems: paginationDefaults.totalItems,
    },
    checkConfirm: [],
    checkReject: [],
    noteSend: "",
    canEdit: false,
    downloadLoading: false,
  });

  useEffect(() => {
    const queryString = buildQueryString(filters.obj, {});
    dispatch(setFilters(queryString));
    getData(queryString.string);
  }, []);

  const getData = async (queryString) => {
    const canEdit = checkRoles(userInfo.roles, expenseRefundDisapprove["disapproveExpenseReport"].edit);
    // const data = await ExpenseRefundApproval.getAlreadyApproved(queryString);
    const data = await ExpenseRefundApproval.getApproved(queryString);

    if (!data.error && data.data) {
      data.data.forEach((item, i) => {
        item.key = i; // This is necessary to remove warning in console for unique key row
        if (item.expenseRefundFolder.projectTask) {
          item.wbsorcdc = `${item.expenseRefundFolder.projectTask.id} ${item.expenseRefundFolder.projectTask.name}`;
        }
        if (item.expenseRefundFolder.costCentre) {
          item.wbsorcdc = `${item.expenseRefundFolder.costCentre.id} ${item.expenseRefundFolder.costCentre.name}`;
        }
        item.employee = `${item.expenseRefundFolder.user.name} ${item.expenseRefundFolder.user.surname}`;
      });
      setState((prevState) => ({
        ...prevState,
        dataTable: data.data,
        pagination: {
          currentPage: data.currentPage ? data.currentPage + 1 : paginationDefaults.page,
          pageSize: get(data, "pageSize", paginationDefaults.size),
          totalItems: get(data, "totalItems", paginationDefaults.totalItems),
        },
        canEdit,
      }));
    } else {
      showError(get(data, "error.messageCode", "ER000"));
    }
  };

  const onSearchEmployee = async (value) => {
    if (value.length >= 3) {
      const usersInternalList = await User.getUsersInternalHrList(`allPages=true&username=${value}`);
      if (!usersInternalList.error && usersInternalList.data) {
        const data = [];
        usersInternalList.data.forEach((item) => {
          data.push({ label: `${item.name} ${item.surname}`, value: item.username });
        });
        setState((prevState) => ({ ...prevState, userInternalSearchList: data }));
      } else {
        showError(get(usersInternalList, "error.messageCode", "ER000"));
      }
    }
  };

  const onSearchCdc = async (value) => {
    if (value.length >= 3) {
      const { error, data } = await CostCentre.costCentre(`allPages=true&id=${value}`);
      if (!error && data) {
        const dataArr = [];
        data.forEach((item) => {
          dataArr.push({ label: `${item.id} | ${item.name}`, value: item.id });
        });
        setState((prevState) => ({
          ...prevState,
          cdcSearchList: dataArr,
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, cdcSearchList: [] }));
    }
  };

  const onSearchWbs = async (value) => {
    if (value.length >= 3) {
      const { error, data } = await ProjectTask.getProjectTasksAndPhasesOfProject(`allPages=true&id=${value}`);
      if (!error && data) {
        const dataArr = [];
        data.forEach((item) => {
          dataArr.push({ label: `${item.id} | ${item.name}`, value: item.id });
        });
        setState((prevState) => ({
          ...prevState,
          wbsSearchList: dataArr,
        }));
      }
    } else {
      setState((prevState) => ({ ...prevState, wbsSearchList: [] }));
    }
  };

  const formikFilter = useFormik({
    initialValues: {
      username: "",
      wbs: "",
      wbsString: "",
      costCentreId: "",
      calcRefund: "",
      noteFolder: "",
      periodStart: "",
      periodEnd: "",
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      let dataFilter = cloneDeep(values);
      dataFilter["page"] = 0;
      if (values.wbs) {
        dataFilter["projectTaskId"] = values.wbs;
      }
      if (values.wbsString) {
        dataFilter["projectTaskId"] = values.wbsString;
      }
      if (values.periodStart) {
        dataFilter.dateSendApprovalStart = dayjs(values.periodStart, dateFormat).format(dbDate);
      }
      if (values.periodEnd) {
        dataFilter.dateSendApprovalEnd = dayjs(values.periodEnd, dateFormat).format(dbDate);
      }
      delete dataFilter.periodStart;
      delete dataFilter.periodEnd;
      delete dataFilter.wbs;
      delete dataFilter.wbsString;
      // remove empty values
      Object.keys(dataFilter).forEach((filterKey) => {
        if (includes(["", null, undefined], dataFilter[filterKey])) {
          delete dataFilter[filterKey];
        }
      });
      const queryString = buildQueryString(filters.obj, dataFilter);
      getData(queryString.string);
    },
  });

  const resetFilter = () => {
    const queryString = buildQueryString({ page: paginationDefaults.page - 1, size: paginationDefaults.size }, {});
    dispatch(setFilters(queryString));
    getData(queryString.string);
  };

  const fieldsFilter = [
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "username",
      name: "username",
      value: formikFilter.values.username,
      label: t("expenseReport.filter.userInternal"),
      placeholder: t("expenseReport.filter.userInternal"),
      onChange: (e) => formikFilter.setFieldValue("username", e),
      onBlur: formikFilter.handleBlur("username"),
      onSearch: onSearchEmployee,
      dataSelect: state.userInternalSearchList,
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "wbs",
      name: "wbs",
      value: formikFilter.values.wbs,
      label: t("expenseReport.filter.projectActivity"),
      placeholder: t("expenseReport.filter.projectActivity"),
      onChange: (e) => formikFilter.setFieldValue("wbs", e),
      onBlur: formikFilter.handleBlur("wbs"),
      onSearch: onSearchWbs,
      dataSelect: state.wbsSearchList,
      disabled: formikFilter.values.wbsString || formikFilter.values.costCentreId,
    },
    {
      type: "input",
      isRequired: false,
      id: "wbsString",
      name: "wbsString",
      value: formikFilter.values.wbsString,
      label: t("expenseReport.filter.wbs"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
      disabled: formikFilter.values.wbs || formikFilter.values.costCentreId,
    },
    {
      type: "selectWithSearch",
      allowClear: true,
      isRequired: false,
      id: "costCentreId",
      name: "costCentreId",
      value: formikFilter.values.costCentreId,
      label: t("expenseReport.filter.cdc"),
      placeholder: t("expenseReport.filter.cdc"),
      onChange: (e) => formikFilter.setFieldValue("costCentreId", e),
      onBlur: formikFilter.handleBlur("costCentreId"),
      onSearch: onSearchCdc,
      dataSelect: state.cdcSearchList,
      disabled: formikFilter.values.wbs || formikFilter.values.wbsString,
    },
    {
      type: "input",
      isRequired: false,
      id: "calcRefund",
      name: "calcRefund",
      value: formikFilter.values.calcRefund,
      label: t("expenseReport.filter.amount"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "input",
      isRequired: false,
      id: "noteFolder",
      name: "noteFolder",
      value: formikFilter.values.noteFolder,
      label: t("expenseReport.filter.folderName"),
      onChange: formikFilter.handleChange,
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "select",
      id: "type",
      name: "type",
      value: formikFilter.values.type,
      label: t("expenseReport.filter.type"),
      onChange: formikFilter.handleChange("type"),
      onBlur: formikFilter.handleBlur("type"),
      dataSelect: [
        { value: "JUSTIFICATION", label: t("expenseReport.filter.optionsType.justification") },
        { value: "FLAT_RATE", label: t("expenseReport.filter.optionsType.flat_rate") },
        { value: "KM", label: t("expenseReport.filter.optionsType.km") },
        { value: "CREDIT_CARD", label: t("expenseReport.filter.optionsType.credit_card") },
      ],
    },
    {
      type: "date",
      allowClear: true,
      isRequired: false,
      id: "periodStart",
      name: "periodStart",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("expenseReport.filter.periodStart"),
      value: formikFilter.values.periodStart,
      onChange: (date) => {
        formikFilter.setFieldValue("periodStart", date);
      },
      onBlur: formikFilter.handleBlur,
    },
    {
      type: "date",
      allowClear: true,
      isRequired: false,
      id: "periodEnd",
      name: "periodEnd",
      format: dateFormat,
      locale: langDate[`locale_${toLower(currentLanguage)}`],
      label: t("expenseReport.filter.periodEnd"),
      value: formikFilter.values.periodEnd,
      onChange: (date) => {
        formikFilter.setFieldValue("periodEnd", date);
      },
      onBlur: formikFilter.handleBlur,
    },
  ];

  const checkReject = (id) => {
    const rejectedArr = cloneDeep(state.checkReject);
    const confirmedArr = cloneDeep(state.checkConfirm);
    if (includes(rejectedArr, id)) {
      remove(rejectedArr, (item) => item === id);
    } else {
      rejectedArr.push(id);
      remove(confirmedArr, (item) => item === id);
    }
    setState((prevState) => ({ ...prevState, checkConfirm: confirmedArr, checkReject: rejectedArr }));
  };

  const cellStyle = {
    style: { maxWidth: 150, whiteSpace: "normal", overflowWrap: "break-word", wordBreak: "break-word" },
  };

  const columns = [
    {
      title: t("expenseReport.table.userInternal"),
      dataIndex: "employee",
      key: "userInternal",
      fixed: "left",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
      responsive: ["md"],
    },
    {
      title: t("expenseReport.table.wbsorcdc"),
      dataIndex: "wbsorcdc",
      key: "wbsorcdc",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.amount"),
      dataIndex: "calcRefund",
      key: "calcRefund",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          <>
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.value
            }
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.suffix
            }
          </>
        </Tooltip>
      ),
      responsive: ["md"],
    },
    {
      title: t("expenseReport.table.folderName"),
      dataIndex: ["expenseRefundFolder", "note"],
      key: "folderName",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.type"),
      dataIndex: "type",
      key: "type",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          {t(`expenseRefundFolder.typologies.${value.toLowerCase()}`)}
        </Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.date"),
      dataIndex: "dateSendApproval",
      key: "date",
      responsive: ["md"],
      onCell: () => cellStyle,
      render: (date, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          {dayjs(date, dbDate).format(dateFormat)}
        </Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.disapprove"),
      dataIndex: "reject",
      key: "reject",
      fixed: "right",
      width: 50,
      className: "bg-red",
      render: (_, record) => {
        return (
          <div className="space-around">
            <Checkbox
              disabled={!state.canEdit}
              onChange={() => checkReject(record.id)}
              checked={includes(state.checkReject, record.id)}
            />
          </div>
        );
      },
    },
    {
      title: t("expenseReport.table.view"),
      dataIndex: "view",
      key: "view",
      fixed: "right",
      width: 50,
      render: (_, record) => {
        return (
          <div className="space-around">
            <EyeOutlined
              className="icon-small"
              onClick={() => setState((prevState) => ({ ...prevState, modalDetail: { visible: true, data: record } }))}
            />
          </div>
        );
      },
    },
  ];

  const columnsSistem_it = [
    {
      title: t("expenseReport.table.userInternal"),
      dataIndex: "employee",
      key: "userInternal",
      fixed: "left",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
      responsive: ["md"],
    },
    {
      title: t("expenseReport.table.wbsorcdc"),
      dataIndex: "wbsorcdc",
      key: "wbsorcdc",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.amount"),
      dataIndex: "amount",
      key: "amount",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          <>
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.value
            }
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.suffix
            }
          </>
        </Tooltip>
      ),
      responsive: ["md"],
    },
    {
      title: t("expenseReport.table.calcRefund"),
      dataIndex: "calcRefund",
      key: "calcRefund",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          <>
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.value
            }
            {
              formatNumber({
                value,
                lang: currentLanguage,
                suffix: "EUR",
              }).props.suffix
            }
          </>
        </Tooltip>
      ),
      responsive: ["md"],
    },
    {
      title: t("expenseReport.table.folderName"),
      dataIndex: ["expenseRefundFolder", "note"],
      key: "folderName",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>{value}</Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.type"),
      dataIndex: "type",
      key: "type",
      className: "table-value-right",
      onCell: () => cellStyle,
      render: (value, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          {t(`expenseRefundFolder.typologies.${value.toLowerCase()}`)}
        </Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.date"),
      dataIndex: "dateSendApproval",
      key: "date",
      onCell: () => cellStyle,
      responsive: ["md"],
      render: (date, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          {dayjs(date, dbDate).format(dateFormat)}
        </Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.status"),
      dataIndex: "status",
      key: "data",
      onCell: () => cellStyle,
      responsive: ["md"],
      render: (status, record) => (
        <Tooltip title={`${t("expenseReport.table.tooltip")} ${record.note}`}>
          {t(`projects.status.${toLower(status)}`)}
        </Tooltip>
      ),
    },
    {
      title: t("expenseReport.table.disapprove"),
      dataIndex: "reject",
      key: "reject",
      width: 50,
      fixed: "right",
      className: "bg-red",
      render: (_, record) => {
        return (
          <div className="space-around">
            <div className="space-around">
              <label className="custom-checkbox-label">
                <input
                  className="display-none"
                  type="checkbox"
                  onChange={() => checkReject(record.id)}
                  checked={includes(state.checkReject, record.id)}
                />
                <span className="custom-checkbox-span"></span>
              </label>
            </div>
          </div>
        );
      },
    },
    {
      title: t("expenseReport.table.view"),
      dataIndex: "view",
      key: "view",
      width: 50,
      fixed: "right",
      render: (_, record) => {
        return (
          <div className="space-around">
            <EyeOutlined
              className="icon-small"
              onClick={() => setState((prevState) => ({ ...prevState, modalDetail: { visible: true, data: record } }))}
            />
          </div>
        );
      },
    },
  ];

  const confirmApproval = async () => {
    const body = [];
    state.checkConfirm.forEach((item) => {
      body.push({ approval: true, id: item, message: null });
    });
    state.checkReject.forEach((item) => {
      body.push({ approval: false, id: item, message: state.noteSend });
    });
    const { error, message } = await ExpenseRefundApproval.confirmApproval(body);
    if (message === "OK") {
      showSuccess();
      setState((prevState) => ({ ...prevState, modal: { visible: false } }));
      const queryString = buildQueryString(filters.obj, {});
      dispatch(setFilters(queryString));
      getData(queryString.string);
    } else {
      showError(get(error, "messageCode", "ER000"));
    }
  };

  const downloadFile = async (doc) => {
    if (doc) {
      setDownloadLoading({ ...downloadLoading, [toLower(doc.type)]: true });
      const file = await ExpenseRefund.downloadDocument(doc.id);
      if (file) {
        const parts = split(doc.filePath, "/");
        const fileName = last(parts);
        const blob = new Blob([file]);
        exportFile(blob, fileName);
        showSuccess();
      } else {
        showError(get(file, "error.messageCode", "ER000"));
      }
      setDownloadLoading({ ...downloadLoading, [toLower(doc.type)]: false });
    }
  };

  // const currentStep = () => {
  //   switch (state.modalDetail.data?.status) {
  //     case "WAITING_APPROVAL_1":
  //       return 1;
  //     case "WAITING_APPROVAL_2":
  //       return 2;
  //     case "WAITING_APPROVAL_3":
  //       return 3;
  //     default:
  //       return 0;
  //   }
  // };

  const modalDetailContent = () => {
    switch (state.modalDetail.data?.type) {
      case "JUSTIFICATION":
        return <Justification modalViewData={state.modalDetail.data} />;
      case "FLAT_RATE":
        return <FlatRate modalViewData={state.modalDetail.data} />;
      case "KM":
        return <Km modalViewData={state.modalDetail.data} userInfo={state.modalDetail.data.expenseRefundFolder.user} />;
      case "CREDIT_CARD":
        return <CreditCard modalViewData={state.modalDetail.data} />;
      default:
        return "";
    }
  };

  const wbsOrCdcName = (item) => {
    if (item.expenseRefundFolder.projectTask) {
      return `${item.expenseRefundFolder.projectTask.id} ${item.expenseRefundFolder.projectTask.name}`;
    } else {
      if (item.expenseRefundFolder.costCentre) {
        return `${item.expenseRefundFolder.costCentre.id} ${item.expenseRefundFolder.costCentre.name}`;
      } else {
        return null;
      }
    }
  };

  return (
    <>
      <Filter formikForm={formikFilter} fields={fieldsFilter} onReset={resetFilter} />

      {/* <Row justify="space-between" className="mb-10">
        <Button
          className={"mr-10"}
          isLoading={state.downloadLoading}
          currentIcon={<DownloadOutlined className="icon-medium" />}
          clickCallback={handleDownload()}
        />
        <Button
          content={"Approva note spese"}
          isDisabled={!state.canEdit || (isEmpty(state.checkConfirm) && isEmpty(state.checkReject))}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true } }))}
          currentIcon={<SendOutlined className="icon-small" />}
        />
      </Row> */}

      {/* <Row align="middle" justify="end" className="mb-10">
        <Col>Selezione massiva:</Col>
        <Button
          isDisabled={!state.canEdit}
          className="ml-10"
          type={"green"}
          clickCallback={() => checkAll("confirm")}
          currentIcon={<CheckOutlined className="icon-small" />}
        />
        <Button
          isDisabled={!state.canEdit}
          className="ml-10"
          type={"red"}
          clickCallback={() => checkAll("reject")}
          currentIcon={<CloseOutlined className="icon-small" />}
        />
      </Row> */}

      <Table
        rowKey={(record) => record.id}
        columns={checkRoles(userInfo.roles, ["SYSTEM_IT"]) ? columnsSistem_it : columns}
        dataSource={state.dataTable}
        style={{
          borderRadius: "8px",
          overflow: "hidden",
        }}
        scroll={{ x: "max-content" }}
        pagination={{
          current: state.pagination.currentPage,
          pageSize: state.pagination.pageSize,
          total: state.pagination.totalItems,
          onChange: (page, pageSize) => {
            const queryString = buildQueryString(filters.obj, { page: page - 1, size: pageSize });
            dispatch(setFilters(queryString));
            setState((prevState) => ({
              ...prevState,
              pagination: {
                currentPage: page,
                pageSize: pageSize,
                totalItems: state.pagination.totalItems,
              },
            }));
            getData(queryString.string);
          },
        }}
      />
      <Row justify="end" className="mb-10">
        <Button
          content={t("expenseReport.disapproveExpenseReport")}
          isDisabled={!state.canEdit || (isEmpty(state.checkConfirm) && isEmpty(state.checkReject))}
          clickCallback={() => setState((prevState) => ({ ...prevState, modal: { visible: true } }))}
          currentIcon={<SendOutlined className="icon-small" />}
        />
      </Row>

      <Modal
        open={state.modalDetail.visible}
        width={700}
        title={`${state.modalDetail.data?.expenseRefundFolder?.note} - ${t(
          `expenseRefundFolder.typologies.${state.modalDetail.data?.type?.toLowerCase()}`
        )}`}
        footer={[]}
        onCancel={() => setState((prevState) => ({ ...prevState, modalDetail: { visible: false } }))}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          <div>
            <Row justify="end" className="mb-10">
              <Button
                content={t("expenseRefundFolder.downloadAttachment.receipt")}
                isDisabled={!state.modalDetail.data?.documents?.find((doc) => doc.type === "RECEIPT")}
                clickCallback={() =>
                  downloadFile(state.modalDetail.data?.documents?.find((doc) => doc.type === "RECEIPT"))
                }
                currentIcon={<DownloadOutlined className="icon-medium" />}
                isLoading={downloadLoading.receipt}
              />
              <Button
                className="ml-10"
                content={t("expenseRefundFolder.downloadAttachment.payment_tracking")}
                isDisabled={!state.modalDetail.data?.documents?.find((doc) => doc.type === "PAYMENT_TRACKING")}
                clickCallback={() =>
                  downloadFile(state.modalDetail.data?.documents?.find((doc) => doc.type === "PAYMENT_TRACKING"))
                }
                currentIcon={<DownloadOutlined className="icon-medium" />}
                isLoading={downloadLoading.payment_tracking}
              />
            </Row>
            {/* <Row>
              <Steps size={"small"} className="mt-10 mb-10" progressDot current={currentStep()}>
                <Step title={t("expenseReport.step.init")} />
                <Step title={t("expenseReport.step.step1")} />
                <Step title={t("expenseReport.step.step2")} />
                <Step title={t("expenseReport.step.step3")} />
              </Steps>
            </Row> */}
            {modalDetailContent()}
          </div>
        )}
      </Modal>
      <Modal
        open={state.modal.visible}
        width={1000}
        title={t("expenseReport.modal.confirmDisapprove")}
        footer={
          <Row justify="end" className="mb-10">
            <Button
              isDisabled={isEmpty(state.noteSend) && !isEmpty(state.checkReject)}
              clickCallback={confirmApproval}
              currentIcon={<CheckOutlined className="icon-small" />}
            />
          </Row>
        }
        onCancel={() => setState((prevState) => ({ ...prevState, modal: { visible: false } }))}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          <div>
            {!isEmpty(state.checkReject) && (
              <>
                <Row>
                  <Col span={24}>
                    <div className="PFred fw-bold mb-10 mt-10">{t("expenseReport.modal.titleDisapprove")}</div>
                  </Col>
                </Row>
                <Row>
                  <Col span={4}>
                    <div className="fw-bold">{t("expenseReport.modal.userInternal")}</div>
                  </Col>
                  <Col span={6}>
                    <div className="fw-bold">{t("expenseReport.modal.wbsorcdc")}</div>
                  </Col>
                  <Col span={3}>
                    <div className="fw-bold">{t("expenseReport.modal.calcRefund")}</div>
                  </Col>
                  {checkRoles(userInfo.roles, ["SYSTEM_IT"]) && (
                    <Col span={3}>
                      <div className="fw-bold">{t("expenseReport.modal.amount")}</div>
                    </Col>
                  )}
                  <Col span={4}>
                    <div className="fw-bold">{t("expenseReport.modal.folderName")}</div>
                  </Col>
                  <Col span={4}>
                    <div className="fw-bold">{t("expenseReport.modal.date")}</div>
                  </Col>
                </Row>

                {state.dataTable.map((item) => {
                  if (includes(state.checkReject, item.id)) {
                    return (
                      <div key={item.key}>
                        <Row>
                          <Col span={4}>{item.employee}</Col>
                          <Col span={6}>{wbsOrCdcName(item)}</Col>
                          <Col span={3}>
                            {formatNumber({
                              value: item.calcRefund,
                              lang: currentLanguage,
                              suffix: "EUR",
                            })}
                          </Col>
                          {checkRoles(userInfo.roles, ["SYSTEM_IT"]) && (
                            <Col span={3}>
                              {formatNumber({
                                value: item.amount,
                                lang: currentLanguage,
                                suffix: "EUR",
                              })}
                            </Col>
                          )}
                          <Col span={4}>{item.note}</Col>
                          <Col span={4}>{dayjs(item.dateSendApproval, dbDateHour).format(dateHourFormat)}</Col>
                        </Row>
                      </div>
                    );
                  }
                })}
                {!isEmpty(state.checkReject) && (
                  <>
                    <div className="fw-bold mt-10">{t("expenseReport.modal.note")}</div>
                    <Row>
                      <TextArea
                        onChange={(e) => setState((prevState) => ({ ...prevState, noteSend: e.target.value }))}
                        value={state.noteSend}
                        maxLength={400}
                      />
                    </Row>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </Modal>
    </>
  );
};

export default DisapproveExpenseReport;
